import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import { FiAlertCircle, FiCheckCircle, FiLoader, FiSearch, FiChevronDown, FiDownload } from 'react-icons/fi';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { SITE_ORIGIN } from './siteConfig';
import { DEFAULT_PAGE_TITLE } from './seoDefaults';

const PATH = '/check-your-seo';
const CANONICAL = `${SITE_ORIGIN}${PATH}`;

function ScoreRing({ value, label, size = 100 }) {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const offset = value != null ? circ - (circ * value) / 100 : circ;
  const color = value == null ? '#94a3b8' : value >= 80 ? '#10b981' : value >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex flex-col items-center gap-1.5">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="8" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="8"
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
          className="fill-slate-900 text-xl font-bold" style={{ fontSize: size * 0.26 }}>
          {value != null ? `${value}%` : '—'}
        </text>
      </svg>
      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{label}</span>
    </div>
  );
}

function CheckList({ title, checks, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!checks || checks.length === 0) return null;
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left font-semibold text-slate-900 text-sm transition-colors"
        aria-expanded={open}>
        {title}
        <FiChevronDown className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <ul className="divide-y divide-slate-100 px-4">
          {checks.map((c, i) => (
            <li key={i} className="flex items-start gap-2 py-2.5 text-sm">
              {c.pass
                ? <FiCheckCircle className="text-emerald-600 shrink-0 mt-0.5" aria-label="Pass" />
                : <FiAlertCircle className="text-amber-600 shrink-0 mt-0.5" aria-label="Fail" />}
              <span className={c.pass ? 'text-slate-700' : 'text-slate-900 font-medium'}>{c.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MetricRow({ label, value, unit, good }) {
  const display = value == null ? '—' : `${value}${unit || ''}`;
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
      <span className="text-slate-700">{label}</span>
      <span className={`font-semibold tabular-nums ${good === true ? 'text-emerald-700' : good === false ? 'text-amber-700' : 'text-slate-900'}`}>
        {display}
      </span>
    </div>
  );
}

export default function CheckYourSeoPage() {
  const urlFieldId = useId();
  const helperId = `${urlFieldId}-helper`;
  const statusId = `${urlFieldId}-status`;
  const resultsRef = useRef(null);

  const [urlInput, setUrlInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [socialImageFailed, setSocialImageFailed] = useState(false);

  useEffect(() => {
    document.title = 'Free SEO audit tool — technical, on-page & Core Web Vitals | WebCodeCraft';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', CANONICAL);
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute('content') : '';
    if (desc) {
      desc.setAttribute(
        'content',
        'Free SEO audit: paste a URL for a scored report covering technical SEO, on-page optimisation, Core Web Vitals and security headers. No login required. UK-based WebCodeCraft.'
      );
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const twTitle = document.querySelector('meta[property="twitter:title"]');
    const twDesc = document.querySelector('meta[property="twitter:description"]');
    const twUrl = document.querySelector('meta[property="twitter:url"]');
    const pageTitle = 'Free SEO audit tool — technical, on-page & Core Web Vitals | WebCodeCraft';
    const pageDesc = 'Free SEO audit: paste a URL for a scored report covering technical SEO, on-page optimisation, Core Web Vitals and security headers. No login required. UK-based WebCodeCraft.';
    if (ogTitle) ogTitle.setAttribute('content', pageTitle);
    if (ogDesc) ogDesc.setAttribute('content', pageDesc);
    if (ogUrl) ogUrl.setAttribute('content', CANONICAL);
    if (twTitle) twTitle.setAttribute('content', pageTitle);
    if (twDesc) twDesc.setAttribute('content', pageDesc);
    if (twUrl) twUrl.setAttribute('content', CANONICAL);

    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Free SEO audit tool — technical, on-page & Core Web Vitals',
      url: CANONICAL,
      description: pageDesc,
      isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN },
      publisher: { '@type': 'Organization', name: 'WebCodeCraft', url: SITE_ORIGIN }
    });
    ld.setAttribute('data-page', 'check-your-seo');
    document.head.appendChild(ld);

    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      if (canonical) canonical.setAttribute('href', `${SITE_ORIGIN}/`);
      if (desc && prevDesc != null) desc.setAttribute('content', prevDesc);
      ld.remove();
    };
  }, []);

  const runAudit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    setData(null);
    const trimmed = urlInput.trim();
    if (!trimmed) { setError('Enter a URL to audit.'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/seo-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: trimmed })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setError(typeof json.error === 'string' ? json.error : 'Audit failed.'); return; }
      setData(json);
      requestAnimationFrame(() => { resultsRef.current?.focus(); });
    } catch { setError('Network error. Try again in a moment.'); }
    finally { setLoading(false); }
  }, [urlInput]);

  useEffect(() => {
    setSocialImageFailed(false);
  }, [data?.ogImageAbsolute, data?.finalUrl]);

  const psi = data?.psi;
  const finalScore = data?.scores?.overall ?? data?.weightedScore?.total ?? 0;
  const fixMySiteHref = `${SITE_ORIGIN}/?site=${encodeURIComponent(data?.finalUrl || '')}#contact`;

  const generatePdf = useCallback(() => {
    if (!data?.ok) return;
    const s = data.scores;
    const w = data.weightedScore;
    const now = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

    const checkRows = (checks) =>
      (checks || []).map((c) => `<tr><td style="padding:4px 8px;font-size:13px">${c.pass ? '✅' : '⚠️'} ${c.label}</td></tr>`).join('');

    const metricRow = (label, val) =>
      val != null ? `<tr><td style="padding:3px 8px;font-size:13px;color:#334155">${label}</td><td style="padding:3px 8px;font-size:13px;font-weight:600;text-align:right">${val}</td></tr>` : '';

    const weightRows = (w?.breakdown || [])
      .map((b) => `<tr><td style="padding:3px 8px;font-size:13px;color:#334155">${b.label}</td><td style="padding:3px 8px;font-size:13px;font-weight:600;text-align:right">${b.earned} / ${b.max}</td></tr>`)
      .join('');

    const html = `<!DOCTYPE html><html lang="en-GB"><head><meta charset="utf-8"><title>SEO Audit — ${data.finalUrl}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:#1e293b;padding:32px 40px;max-width:800px;margin:0 auto;font-size:14px;line-height:1.5}
h1{font-size:22px;margin-bottom:2px}h2{font-size:15px;margin:20px 0 6px;border-bottom:1px solid #e2e8f0;padding-bottom:4px}
.brand{color:#4f46e5;font-size:12px;font-weight:600;letter-spacing:0.5px;text-transform:uppercase;margin-bottom:12px}
.scores{display:flex;gap:24px;margin:16px 0 20px}
.score-box{text-align:center;padding:12px 18px;border:1px solid #e2e8f0;border-radius:10px;min-width:100px}
.score-box .num{font-size:28px;font-weight:700}.score-box .lbl{font-size:11px;text-transform:uppercase;color:#64748b;letter-spacing:0.4px}
.green{color:#059669}.amber{color:#d97706}.red{color:#dc2626}
table{width:100%;border-collapse:collapse}
table tr:nth-child(even){background:#f8fafc}
.footer{margin-top:28px;padding-top:14px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;text-align:center}
.footer a{color:#4f46e5;text-decoration:none}
.cta{display:inline-block;margin-top:18px;padding:10px 24px;background:#4f46e5;color:#fff;border-radius:8px;text-decoration:none;font-weight:600;font-size:14px}
@media print{body{padding:20px 24px}.scores{gap:16px}}
</style></head><body>
<div class="brand">WebCodeCraft — SEO Audit Report</div>
<h1>SEO Audit Results</h1>
<p style="color:#64748b;font-size:13px;margin-bottom:4px">${data.finalUrl}</p>
<p style="color:#94a3b8;font-size:12px">${now}</p>
<div class="scores">
<div class="score-box"><div class="num ${s.overall >= 80 ? 'green' : s.overall >= 50 ? 'amber' : 'red'}">${s.overall}%</div><div class="lbl">SEO score (weighted)</div></div>
<div class="score-box"><div class="num ${s.technical >= 80 ? 'green' : s.technical >= 50 ? 'amber' : 'red'}">${s.technical}%</div><div class="lbl">Technical</div></div>
<div class="score-box"><div class="num ${s.onPage >= 80 ? 'green' : s.onPage >= 50 ? 'amber' : 'red'}">${s.onPage}%</div><div class="lbl">On-page</div></div>
<div class="score-box"><div class="num ${s.visibility != null ? (s.visibility >= 80 ? 'green' : s.visibility >= 50 ? 'amber' : 'red') : ''}">${s.visibility != null ? s.visibility + '%' : '—'}</div><div class="lbl">Visibility</div></div>
</div>
<h2>Weighted score breakdown</h2>
<table>${weightRows}</table>
<h2>Social preview (Open Graph)</h2>
<table>${metricRow('og:title', data.ogTitle || '—')}${metricRow('og:image (resolved)', data.ogImageAbsolute || '—')}</table>
<h2>Schema</h2>
<table>${metricRow('LocalBusiness', data.schemaFlags?.hasLocalBusiness ? 'Found' : 'Not found')}${metricRow('Organization', data.schemaFlags?.hasOrganization ? 'Found' : 'Not found')}</table>
<h2>Images</h2>
<table>${metricRow('With non-empty alt', data.imagesAltPercent != null ? data.imagesAltPercent + '%' : '—')}${metricRow('Images checked', data.images?.total)}${metricRow('Missing meaningful alt', data.imagesMissingAltCount)}</table>
<h2>Page Snapshot</h2>
<table>${metricRow('Final URL', data.finalUrl)}${metricRow('Redirects', data.redirectCount)}${metricRow('Response time', data.responseTimeMs + ' ms')}${metricRow('HTTPS', data.isHttps ? 'Yes' : 'No')}${metricRow('Word count', data.wordCount)}</table>
<h2>Technical SEO</h2><table>${checkRows(data.technicalChecks)}</table>
<h2>On-page SEO</h2><table>${checkRows(data.onPageChecks)}</table>
<h2>Performance &amp; Visibility</h2><table>${checkRows(data.visibilityChecks)}</table>
${psi ? `<h2>Core Web Vitals (mobile, lab)</h2><table>${metricRow('Performance', psi.performanceScore + '%')}${metricRow('LCP', psi.lcp != null ? (psi.lcp / 1000).toFixed(1) + ' s' : '—')}${metricRow('CLS', psi.cls != null ? psi.cls : '—')}${metricRow('INP', psi.inp != null ? psi.inp + ' ms' : '—')}${metricRow('FCP', psi.fcp != null ? (psi.fcp / 1000).toFixed(1) + ' s' : '—')}${metricRow('TBT', psi.tbt != null ? psi.tbt + ' ms' : '—')}${metricRow('Mobile-friendly', psi.mobileFriendly ? 'Yes' : 'No')}</table>` : ''}
<div style="text-align:center"><a class="cta" href="${(w?.total ?? s.overall) < 70 ? `https://webcodecraft.co.uk/?site=${encodeURIComponent(data.finalUrl)}#contact` : 'https://webcodecraft.co.uk/#contact'}">${(w?.total ?? s.overall) < 70 ? 'Fix my site — get in touch' : 'Get in touch'}</a></div>
<div class="footer"><p>Report generated by <a href="https://webcodecraft.co.uk/check-your-seo">WebCodeCraft Free SEO Audit</a></p><p style="margin-top:4px">webcodecraft.co.uk &middot; hello@webcodecraft.info &middot; +44 7864 841709</p></div>
</body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWin = window.open(url, '_blank');
    if (printWin) {
      printWin.addEventListener('afterprint', () => URL.revokeObjectURL(url));
      printWin.addEventListener('load', () => { printWin.focus(); printWin.print(); });
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = `seo-audit-${new URL(data.finalUrl).hostname}.html`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }
  }, [data, psi]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <Header />
      <CookieBanner />

      <main id="main-content" className="pt-20 min-h-screen min-w-0 overflow-x-clip">
        <section className="container mx-auto px-4 py-10 max-w-3xl" aria-labelledby="seo-audit-heading">
          <m.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="text-sm font-medium text-indigo-600 mb-2">
              <Link to="/website-tax-calculator" className="underline underline-offset-2 hover:text-indigo-800">Tools</Link>
              <span className="mx-1 text-slate-400" aria-hidden="true">/</span>
              <span className="text-slate-700">SEO audit</span>
            </p>
            <h1 id="seo-audit-heading" className="text-3xl sm:text-4xl font-bold text-slate-900 font-display tracking-tight mb-4">
              Free SEO audit tool
            </h1>
            <p className="text-slate-600 leading-relaxed mb-6" id={helperId}>
              Paste a <strong>public</strong> URL below. We fetch the HTML, check robots.txt, sitemap, security
              headers, and call the PageSpeed Insights API for Core Web Vitals — then score your page across
              <strong> technical SEO</strong>, <strong>on-page optimisation</strong>, and{' '}
              <strong>performance &amp; visibility</strong>. No login. No data stored.
            </p>

            <form onSubmit={runAudit} className="space-y-4" noValidate>
              <div>
                <label htmlFor={urlFieldId} className="block text-sm font-semibold text-slate-800 mb-1.5">Page URL</label>
                <input id={urlFieldId} type="url" name="url" inputMode="url" autoComplete="url"
                  placeholder="https://example.com/your-page" value={urlInput}
                  onChange={(ev) => setUrlInput(ev.target.value)}
                  className="form-input w-full max-w-xl" aria-describedby={helperId}
                  aria-invalid={error ? 'true' : 'false'} disabled={loading} />
              </div>
              <m.button type="submit" className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                disabled={loading} whileTap={{ scale: 0.98 }}>
                {loading
                  ? <><FiLoader className="animate-spin" aria-hidden="true" />Auditing…</>
                  : <><FiSearch aria-hidden="true" />Run audit</>}
              </m.button>
            </form>

            <div id={statusId} role="status" aria-live="polite" className="mt-6 min-h-[1.25rem] text-sm">
              {error && (
                <p className="text-red-700 font-medium flex items-start gap-2">
                  <FiAlertCircle className="shrink-0 mt-0.5" aria-hidden="true" />{error}
                </p>
              )}
              {loading && (
                <p className="text-slate-500">Fetching page, robots.txt, sitemap &amp; PageSpeed data — this can take up to 30 seconds…</p>
              )}
            </div>
          </m.div>
        </section>

        {data?.ok && (
          <section ref={resultsRef} tabIndex={-1}
            className="container mx-auto px-4 pb-16 max-w-3xl outline-none space-y-8"
            aria-label="SEO audit results">

            <div className="flex flex-col items-center gap-4 py-4">
              <ScoreRing value={data.scores.overall} label="SEO score" size={140} />
              <p className="text-xs text-slate-500 text-center max-w-md">
                Weighted score (100 pts): title, H1, HTTPS, meta description, canonical, Open Graph, business schema, image alt coverage, robots.txt.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-600">
                <span><span className="font-semibold text-slate-800">{data.scores.technical}%</span> technical</span>
                <span aria-hidden="true" className="text-slate-300">·</span>
                <span><span className="font-semibold text-slate-800">{data.scores.onPage}%</span> on-page</span>
                <span aria-hidden="true" className="text-slate-300">·</span>
                <span><span className="font-semibold text-slate-800">{data.scores.visibility != null ? `${data.scores.visibility}%` : '—'}</span> visibility</span>
              </div>
            </div>

            {data.weightedScore?.breakdown?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 sm:px-6 py-4">
                <h2 className="text-lg font-bold text-slate-900 mb-3">Score breakdown</h2>
                <ul className="divide-y divide-slate-100 text-sm">
                  {data.weightedScore.breakdown.map((b) => (
                    <li key={b.id} className="flex justify-between py-2 gap-4">
                      <span className="text-slate-700">{b.label}</span>
                      <span className="font-semibold tabular-nums text-slate-900 shrink-0">{b.earned}<span className="text-slate-400 font-normal">/{b.max}</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 sm:px-6 py-3 border-b border-slate-100 bg-slate-50">
                <h2 className="text-lg font-bold text-slate-900">Social preview</h2>
                <p className="text-xs text-slate-500 mt-0.5">Open Graph (how links may appear when shared)</p>
              </div>
              <div className="border-b border-slate-100 bg-slate-100/80">
                {data.ogImageAbsolute && !socialImageFailed ? (
                  <img
                    src={data.ogImageAbsolute}
                    alt={data.ogTitle || data.title || ''}
                    className="w-full max-h-52 object-cover object-center bg-slate-200"
                    onError={() => setSocialImageFailed(true)}
                  />
                ) : data.ogImageAbsolute && socialImageFailed ? (
                  <p className="px-4 py-3 text-sm text-slate-600">Preview image failed to load.</p>
                ) : data.ogImage ? (
                  <p className="px-4 py-3 text-sm text-amber-800 bg-amber-50">og:image could not be resolved to a safe absolute URL.</p>
                ) : (
                  <p className="px-4 py-3 text-sm text-slate-500">No og:image meta tag found.</p>
                )}
              </div>
              <div className="px-4 sm:px-6 py-4">
                <p className="font-semibold text-slate-900 leading-snug">{data.ogTitle || data.title || '—'}</p>
                <p className="text-xs text-slate-500 mt-1 truncate" title={data.finalUrl}>{new URL(data.finalUrl).hostname}</p>
                {data.ogTitle ? null : (
                  <p className="text-xs text-amber-700 mt-2">No og:title — showing page title if available.</p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 sm:px-6 py-4">
              <h2 className="text-lg font-bold text-slate-900 mb-3">Schema (JSON-LD)</h2>
              <MetricRow label="LocalBusiness" value={data.schemaFlags?.hasLocalBusiness ? 'Found' : 'Not found'} good={data.schemaFlags?.hasLocalBusiness} />
              <MetricRow label="Organization" value={data.schemaFlags?.hasOrganization ? 'Found' : 'Not found'} good={data.schemaFlags?.hasOrganization} />
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 sm:px-6 py-4">
              <h2 className="text-lg font-bold text-slate-900 mb-3">Image audit</h2>
              <MetricRow
                label="Images with meaningful alt text"
                value={data.images?.total > 0 ? data.imagesAltPercent : 100}
                unit="%"
                good={data.images?.total === 0 || data.imagesAltPercent >= 80}
              />
              <MetricRow label="Images scanned (max 200)" value={data.images?.total} />
              <MetricRow label="Missing meaningful alt" value={data.imagesMissingAltCount} good={data.imagesMissingAltCount === 0} />
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 sm:px-6 py-4">
              <h2 className="text-lg font-bold text-slate-900 mb-3">Page snapshot</h2>
              <MetricRow label="Requested URL" value={data.requestedUrl} />
              <MetricRow label="Final URL" value={data.finalUrl} />
              <MetricRow label="Redirects" value={data.redirectCount} good={data.redirectCount <= 1} />
              <MetricRow label="Response time" value={data.responseTimeMs} unit=" ms" good={data.responseTimeMs < 2000} />
              <MetricRow label="HTTPS" value={data.isHttps ? 'Yes' : 'No'} good={data.isHttps} />
              <MetricRow label="Word count" value={data.wordCount} good={data.wordCount >= 300} />
            </div>

            <CheckList title="Technical SEO checks" checks={data.technicalChecks} defaultOpen />
            <CheckList title="On-page SEO checks" checks={data.onPageChecks} defaultOpen />
            <CheckList title="Performance & visibility checks" checks={data.visibilityChecks} defaultOpen={Boolean(data.psi)} />

            {psi && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 sm:px-6 py-4">
                <h2 className="text-lg font-bold text-slate-900 mb-3">Core Web Vitals (mobile, lab)</h2>
                <MetricRow label="Performance score" value={psi.performanceScore} unit="%" good={psi.performanceScore >= 90} />
                <MetricRow label="LCP" value={psi.lcp != null ? (psi.lcp / 1000).toFixed(1) : null} unit=" s" good={psi.lcp != null && psi.lcp <= 2500} />
                <MetricRow label="CLS" value={psi.cls} good={psi.cls != null && psi.cls <= 0.1} />
                <MetricRow label="INP" value={psi.inp} unit=" ms" good={psi.inp != null && psi.inp <= 200} />
                <MetricRow label="FCP" value={psi.fcp != null ? (psi.fcp / 1000).toFixed(1) : null} unit=" s" good={psi.fcp != null && psi.fcp <= 1800} />
                <MetricRow label="Speed Index" value={psi.speedIndex != null ? (psi.speedIndex / 1000).toFixed(1) : null} unit=" s" good={psi.speedIndex != null && psi.speedIndex <= 3400} />
                <MetricRow label="TBT" value={psi.tbt} unit=" ms" good={psi.tbt != null && psi.tbt <= 200} />
                <MetricRow label="Mobile-friendly" value={psi.mobileFriendly ? 'Yes' : 'No'} good={psi.mobileFriendly} />
              </div>
            )}

            {data.h1Texts && data.h1Texts.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">H1 text</h3>
                <ol className="list-decimal list-inside space-y-1.5 text-slate-700 bg-slate-50 rounded-lg px-4 py-3 border border-slate-200 text-sm">
                  {data.h1Texts.map((t, i) => <li key={i}>{t}</li>)}
                </ol>
              </div>
            )}

            {data.jsonLdTypes && data.jsonLdTypes.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">Structured data types found</h3>
                <ul className="flex flex-wrap gap-2">
                  {data.jsonLdTypes.map((t, i) => (
                    <li key={i} className="bg-indigo-50 text-indigo-800 text-xs font-medium px-2.5 py-1 rounded-full border border-indigo-200">{t}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-3 border-t border-slate-200 pt-6">
              <m.button type="button" onClick={generatePdf} whileTap={{ scale: 0.97 }}
                className="btn btn-primary inline-flex items-center gap-2">
                <FiDownload aria-hidden="true" />Download PDF report
              </m.button>
              {finalScore < 70 && (
                <a
                  href={fixMySiteHref}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors shadow-sm">
                  Fix my site
                </a>
              )}
            </div>

            <p className="text-sm text-slate-500 pt-3">
              Scores are indicative — treat them as a starting point alongside{' '}
              <a href="https://search.google.com/search-console/about" className="text-indigo-600 underline underline-offset-2" target="_blank" rel="noopener noreferrer">Search Console</a>
              {' '}and{' '}
              <a href="https://pagespeed.web.dev/" className="text-indigo-600 underline underline-offset-2" target="_blank" rel="noopener noreferrer">PageSpeed Insights</a>.
            </p>
          </section>
        )}

        <Footer />
      </main>
    </div>
  );
}
